'use client';

import React from "react";
import { fetchMeetings } from "../../api/fetchMeetings";
import { Meeting } from "../../models/meetings.model";
import { format } from "date-fns";
import { hu } from "date-fns/locale";

export default function MeetingList() {
    const [meetingList, setMeetingList] = React.useState<Meeting[]>([]);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchMeetings();
                setMeetingList(response);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    return (
        <div className="p-8 font-sans bg-[#fbfbfd] min-h-screen text-[#1d1d1f]">
            <header className="mb-10 flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-semibold tracking-tight">Aktuális Találkozók</h1>
                    <p className="text-gray-500 mt-1 font-medium italic">Soronkövetkező személyes találkozók Ügyfelekkel.</p>
                </div>
                <div className="bg-[#0A192F] text-[#E6D5B8] px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    {meetingList.length} Foglalás
                </div>
            </header>

            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A192F]"></div>
                </div>
            ) : meetingList.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 max-w-[1600px]">
                    {meetingList.map((meeting) => (
                        <div 
                            key={meeting.id} 
                            className="bg-[#0A192F] rounded-[32px] overflow-hidden shadow-xl border border-white/5 flex flex-col transition-transform hover:scale-[1.02]"
                        >
                            {/* Kártya Fejléc - Időpont */}
                            <div className="bg-[#112240] p-6 border-b border-white/5 flex justify-between items-center">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#E6D5B8]/10 flex items-center justify-center text-xl">
                                        📅
                                    </div>
                                    <div>
                                        <p className="text-[#E6D5B8] font-bold text-lg leading-none">
                                            {/* Itt a new Date() automatikusan kezeli a +2 órát a böngészőben! */}
                                            {format(new Date(meeting.date), 'EEEE', { locale: hu })}
                                        </p>
                                        <p className="text-[#E6D5B8]/50 text-xs uppercase tracking-widest mt-1 font-bold">
                                            {format(new Date(meeting.date), 'PPP', { locale: hu })}
                                        </p>
                                    </div>
                                </div>
                                <div className="bg-[#E6D5B8] text-[#0A192F] px-3 py-1.5 rounded-lg font-black text-lg">
                                    {format(new Date(meeting.date), 'HH:mm')}
                                </div>
                            </div>

                            {/* Kártya Test - Ügyfél adatok */}
                            <div className="p-6 space-y-4 flex-grow">
                                <div>
                                    <label className="text-[10px] uppercase tracking-widest text-[#E6D5B8]/40 font-bold">Ügyfél neve</label>
                                    <p className="text-[#E6D5B8] text-xl font-semibold">{meeting.customerName}</p>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-1">
                                        <label className="text-[10px] uppercase tracking-widest text-[#E6D5B8]/40 font-bold">Körzet</label>
                                        <p className="text-[#E6D5B8]/80 text-sm font-medium">{meeting.districtName}</p>
                                    </div>
                                    <div className="flex-1">
                                        <label className="text-[10px] uppercase tracking-widest text-[#E6D5B8]/40 font-bold">E-mail</label>
                                        <p className="text-[#E6D5B8]/80 text-sm truncate">{meeting.email}</p>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <label className="text-[10px] uppercase tracking-widest text-[#E6D5B8]/40 font-bold">Cím</label>
                                    <p className="text-[#E6D5B8]/90 text-sm leading-relaxed italic">{meeting.address || "Nincs megadva cím"}</p>
                                </div>

                                {meeting.notes && (
                                    <div className="p-3 bg-white/5 rounded-xl border border-white/5">
                                        <label className="text-[10px] uppercase tracking-widest text-[#E6D5B8]/40 font-bold block mb-1">Megjegyzés</label>
                                        <p className="text-[#E6D5B8]/70 text-xs italic">{meeting.notes}</p>
                                    </div>
                                )}
                            </div>

                            {/* Kártya Footer - Akciók */}
                            <div className="p-4 bg-[#112240]/50 border-t border-white/5 flex gap-2">
                                <a 
                                    href={`mailto:${meeting.email}`}
                                    className="flex-1 py-2 rounded-xl bg-white/5 text-[#E6D5B8] text-xs font-bold text-center hover:bg-white/10 transition-colors"
                                >
                                    E-mail küldése
                                </a>
                                <button 
                                    className="px-4 py-2 rounded-xl bg-[#E6D5B8] text-[#0A192F] text-xs font-bold hover:bg-white transition-colors"
                                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(meeting.address)}`, '_blank')}
                                >
                                    📍 Útvonal
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="bg-white rounded-3xl p-20 text-center border border-dashed border-gray-300">
                    <p className="text-gray-400 font-medium italic">Jelenleg nincs aktív foglalás.</p>
                </div>
            )}
        </div>
    );
}